import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { Checkbox, Col, Row } from "antd";
import styles from "./SettingsPage.scss";
import LeftMenu from "../../components/LeftMenu";
import confirmModal from "../../services/confirmModal";
import { requireUserLogin } from "../../routes/Auth/HOCs";
import {
  getUserProfile,
  getUserSettings,
  updateUserSettings
} from "../../routes/Auth/auth.ducks";
import HelmetComponent from "../../utils/HelmetHelper";
import { UserI } from "../../typings/interfaces";

interface SettingsPageProps {
  history: History;
  location: Location;
  match: {
    path: string;
    url: string;
    isExact: boolean;
    params: {};
  };
  user: UserI;
  settings: {
    showAddress: boolean;
    showPhone: boolean;
    receiveNotifications: boolean;
    receivePriceReductionNotifications: boolean;
  };
  getUserSettings: () => void;
  updateUserSettings: ({ }, callback: () => void) => void;
  getUserProfile: (
    params: {},
    callback: () => void,
    forceUpdate?: boolean
  ) => void;
}

const groups = {
  notifications: "notifications",
  display: "display",
  security: "security"
}

const optionGroups = [
  {
    id: groups.notifications,
    name: "Obavijesti"
  },
  {
    id: groups.display,
    name: "Prikaz"
  },
  {
    id: groups.security,
    name: "Sigurnost"
  }
];

const SettingsPage = (props: SettingsPageProps) => {
  const {
    settings,
    match: { path }
  } = props;

  const shouldUpdate = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState([
    {
      key: "receiveNews",
      title: "Želim primati obavijesti o novostima na Index Oglasima",
      description: "Ako nastaviš, više nećeš primati informacije o novim funkcionalnostima, poboljšanjima i važnim novostima na Index Oglasima.",
      checked: false,
      group: groups.notifications,
      condition: true
    },
    {
      key: "receiveSpecialOffers",
      title: "Želim primati posebne personalizirane ponude",
      description: "Ako nastaviš, više nećeš primati prilagođene ponude i korisne informacije o oglasima koji te zanimaju. To znači da možeš propustiti ekskluzivne ponude i važne obavijesti.",
      checked: false,
      group: groups.notifications,
      condition: true
    },
    {
      key: "participateInSurveys",
      title: "Želim sudjelovati u anketama",
      description: "Ako nastaviš, nećeš moći sudjelovati u istraživanjima i anketama koje nam pomažu poboljšati Index Oglase i prilagoditi uslugu tvojim potrebama.",
      checked: false,
      group: groups.notifications,
      condition: true
    },
    {
      key: "receiveXMLSyncNotifications",
      title: "Želim primati obavijesti o mojoj XML sinhronizaciji",
      description: "Ako nastaviš, više nećeš primati obavijesti o uspješnoj XML sinkronizaciji. I dalje ćeš dobivati obavijesti ako sinkronizacija ne uspije.",
      checked: false,
      group: groups.notifications,
      condition: user => user.isAgency
    },
    {
      key: "receiveSavedSearchNotifications",
      title: "Želim primati obavijesti o novim oglasima za svoje spremljene pretrage",
      description: "Ako nastaviš, više nećeš primati obavijesti o novim oglasima koji odgovaraju tvojim spremljenim pretragama.",
      checked: false,
      group: groups.notifications,
      condition: true
    },
    {
      key: "receiveExpiredAdsNotifications",
      title: "Želim primati upozorenje o mojim isteklim oglasima",
      description: "Ako nastaviš, više nećeš dobivati obavijesti kada se tvoji oglasi približavaju isteku niti nakon što isteknu.",
      checked: false,
      group: groups.notifications,
      condition: true
    },
    {
      key: "receivePriceChangeNotifications",
      title: "Želim primati obavijesti o promjenama cijena oglasa koje sam spremio",
      description: "Ako nastaviš, više nećeš dobivati obavijesti kada se snizi cijena oglasa koji si spremio.",
      checked: false,
      group: groups.notifications,
      condition: true
    },
    {
      key: "showAddress",
      title: "Prikaži adresu",
      description: "Ako nastaviš, uklonit ćeš prikaz adrese sa stranice na kojoj se nalaze svi tvoji oglasi.",
      checked: false,
      group: groups.display,
      condition: true
    },
    {
      key: "showPhone",
      title: "Prikaži broj telefona",
      description: "Ako nastaviš, tvoj broj telefona više neće biti vidljiv u oglasima ni na stranici sa svim tvojim oglasima.",
      checked: false,
      group: groups.display,
      condition: true
    },
    {
      key: "twoFactorEnabled",
      title: "Dvofaktorska autentikacija (2FA)",
      description: "Dvofaktorska autentikacija dodaje dodatni sloj sigurnosti tvom računu.",
      checked: false,
      group: groups.security,
      condition: true
    },

  ]);



  useEffect(() => {
    props.getUserSettings();
  }, []);


  useEffect(() => {
    if (settings) {
      const currentSettings = options;
      const newSettings = currentSettings.map(item => {
        if (settings[item.key] !== undefined) {
          // eslint-disable-next-line no-param-reassign
          item.checked = settings[item.key];
        }

        return item;
      });

      setOptions(newSettings);
    }

  }, [settings]);

  useEffect(() => {
    if (shouldUpdate.current) {
      handleSubmit();
    }
  }, [options]);

  const preventDefaultClick = (event: any) => {
    if (!event) {
      return;
    }
    event.preventDefault();
  };

  const handleCheckBoxChange = event => {
    const { name, checked } = event.target;
    const currentOptions = options;
    const localOptions = [...currentOptions];
    const objectChanged = localOptions.find(item => item.key === name);
    objectChanged.checked = checked;
    shouldUpdate.current = true;
    setOptions(localOptions);
  };

  const handleSubmit = async () => {
    setIsLoading(prev => !prev);
    const newSettings = {};

    options.forEach(item => {
      newSettings[item.key] = item.checked;
    });

    await props.updateUserSettings(newSettings, async () => {
      await props.getUserProfile({}, null, true)
      setIsLoading(false);
    });
  };

  const handleCheckBoxChangeConfirmation = (event: any) => {
    const { name, checked } = event.target;
    const option = options.find(item => item.key === name);

    // Special handling for twoFactorEnabled - show modal for both enable and disable
    if (name === "twoFactorEnabled") {
      preventDefaultClick(event);
      const title = checked
        ? "Uključujem dvofaktorsku autentikaciju (2FA)"
        : "Isključujem dvofaktorsku autentikaciju (2FA)";
      const description = checked
        ? "Ako nastaviš, prilikom svake prijave na račun morat ćeš upisati jednokratni kod koji ćemo ti poslati na email adresu."
        : "Ako nastaviš, više nećeš morati upisivati jednokratni kod prilikom prijave na svoj račun.";

      confirmModal.show({
        title,
        okText: "U redu",
        cancelText: "Odustani",
        iconType: "NaN",
        onOk() {
          handleCheckBoxChange(event);
        },
        children: description
      });
      return;
    }

    if (checked) {
      handleCheckBoxChange(event);
      return;
    }

    preventDefaultClick(event);
    confirmModal.show({
      title: option.title || "",
      okText: "U redu",
      cancelText: "Odustani",
      iconType: "NaN",
      onOk() {
        handleCheckBoxChange(event);
      },
      children: option.description || ""
    });
  };

  return (
    <LeftMenu activeKey={path}>
      <HelmetComponent
        title={`${"Postavke"} | ${"INDEX OGLASI"}`}
        index={false}
      />

      <React.Fragment>
        <div className="page-title">{"Postavke"}</div>
        <div className={styles.container}>
          <div className={styles.fieldsWrapper}>
            <Row className={styles.row} gutter={{ xs: 24, xl: 128,}}>
              <Col
                xs={24}
                xl={14}
              > 
                {optionGroups.map(group => (
                  <div key={group.id}
                    className={styles.group}>
                    <div className={styles.groupTitle}>{group.name}</div>
                    <Row>
                      {options
                        .filter(item => item.group === group.id)
                        .map(item => {
                          if (typeof item.condition === "function" && !item.condition(props.user)) {
                            return null;
                          }

                          return (
                            <Col className={styles.checkBox} key={item.key} xs={24} xl={24}>
                              <Checkbox
                                key={item.key}
                                name={item.key}
                                checked={item.checked}
                                onChange={handleCheckBoxChangeConfirmation}
                              >
                                <span className={styles.label}>{item.title}</span>
                              </Checkbox>
                            </Col>
                          )
                        })}
                    </Row>
                  </div>

                ))}
                </Col> 

            </Row>

          </div>
        </div>
      </React.Fragment>
    </LeftMenu>
  );
};

const mapStateToProps = state => ({
  settings: state.auth.settings
});

const mapDispatchToProps = dispatch => ({
  getUserProfile: (params, callback, forceUpdate) =>
    dispatch(getUserProfile(params, callback, forceUpdate)),
  getUserSettings: callback => dispatch(getUserSettings(callback)),
  updateUserSettings: (settings, callback) =>
    dispatch(updateUserSettings(settings, callback))
});

const ConnectedSettingsPage = connect(
  mapStateToProps,
  mapDispatchToProps
)(SettingsPage);

export default requireUserLogin(ConnectedSettingsPage);



// WEBPACK FOOTER //
// ./src/containers/SettingsPage/index.tsx